import classnames from 'classnames';
import { action, computed, observable, runInAction } from 'mobx';
import { observer } from 'mobx-react';
import React from 'react';

// import { FaTrash } from "react-icons/fa";
// import { EditIcon } from "src/ui/common/icons";
// import { Button } from "src/ui/widgets/button/button";

import { DbKey, makeWithDbId, WithDbId } from '../../../../../adl-gen/common/db';
import {
  LearningProvider,
  makeInternationalized,
  makeWorkRole,
  Owner,
  RoleRecommendationMethod,
  WorkRole
} from '../../../../../adl-gen/totalinteraction/futurefit/db';
import { WorkRoleListEntry } from '../../../../../adl-gen/totalinteraction/futurefit/frontend';
import { WorkRoleReferenceData } from '../../../../../adl-gen/totalinteraction/futurefit/requests';
import { LangCode, withI18n, WithI18nProps } from '../../../../../internationalization/i18n-hoc';
import { RadioItem } from '../../../../../ui/widgets/radio-item/radio-item';
import { SelectBox, SelectOption } from '../../../../../ui/widgets/select/select';
import { newNonEmptyTextField } from '../../../../../ui/widgets/text-input/text-field';
import { WorkRoleStore, ScoreRangeUpdate, WorkRoleUpdate } from '../../../../stores';
import { } from '../../../../stores/work-role-store';
import {
  deleteHandler,
  I18nInput,
  I18nTextInputObserver,
  MultiSelectObserver,
  renderButtons,
  SelectObserver
} from '../../../common/editor';
import { FormMode } from '../../../common/form';

import { ScoreRangeEditor } from './score-range-editor';

const styles = require('../../../common/editor.css');
// const buttonStyles = require("../../../common/common.css");

interface WorkRoleEditorProps extends WithI18nProps {
  debug?: boolean;
  item?: WorkRoleListEntry;
  roleData: WorkRoleReferenceData;
  store: WorkRoleStore;
  onCreate(item: WorkRoleUpdate): Promise<DbKey<WorkRole>>;
  onUpdate?(item: WorkRoleUpdate): Promise<void>;
  onClone?(item: WorkRoleUpdate): Promise<DbKey<WorkRole>>;
  onCancel(): void;
  role: 'super' | 'client-admin';
  mode: FormMode;
}

interface WorkRoleFieldState {
  name: I18nInput;
  shortname: I18nInput;
  owner: Owner;

  lang: LangCode;

  scoreRangeUpdates: ScoreRangeUpdate[];

  /// Recommendation method to be used with this WorkRole
  recommendationMethod: RoleRecommendationMethod;
  recommendationProviders: DbKey<LearningProvider>[];
}

/**
 * Component to maintain WorkRoles and WorkRoleScoreRanges.
 */

@observer
class WorkRoleEditor0 extends React.Component<WorkRoleEditorProps> {
  @observable fieldState: WorkRoleFieldState = {
    name: makeInternationalized({ en: newNonEmptyTextField() }),
    shortname: makeInternationalized({ en: newNonEmptyTextField() }),
    owner:
      this.props.role === 'super'
        ? { kind: 'global' }
        : { kind: 'appClientId', value: this.props.store.clientId || '' },
    lang: this.props.currentLangCode(),
    scoreRangeUpdates: [],
    recommendationMethod: RoleRecommendationMethod.relevance,
    recommendationProviders: []
  };

  @observable locked = false;

  constructor(props: WorkRoleEditorProps) {
    super(props);
    const { initI18nEmpty: initEmpty, initI18nFromInput: initFromInput, item, mode, role } = props;
    const state = this.fieldState;
    if (item) {
      /// If mode = update, act as normal. Otherwise, populate the fields as if for an
      /// update, but in fact it's a create.
      if (mode === 'clone') {
        initFromInput(state.name, {
          kind: 'text',
          value: makeInternationalized({ en: `CLONE ${item.name.value.en}` })
        });
        initFromInput(state.shortname, {
          kind: 'text',
          value: makeInternationalized({ en: `CLONE ${item.shortname.value.en}` })
        });
        /// If this a 'Save As' cloned role, all ranges are Creates
        /// - otherwise they don't get created
        state.scoreRangeUpdates = item.scoreRanges.map(range => ({
          kind: 'create',
          value: range.value
        }));
      } else {
        initFromInput(state.name, item.name);
        initFromInput(state.shortname, item.shortname);
        state.scoreRangeUpdates = item.scoreRanges.map(range => ({
          kind: 'update',
          value: range
        }));
      }

      role === 'client-admin' && mode === 'clone'
        ? /// Override
        (state.owner = { kind: 'appClientId', value: this.props.store.clientId || '' })
        : (state.owner = item.owner);

      state.recommendationMethod = item.recommendationMethod;
      state.recommendationProviders = item.recommendationProviders;
    } else {
      state.name = initEmpty(state.name);
      state.shortname = initEmpty(state.shortname);
    }
  }

  @action lockUpdates = () => (this.locked = true);

  @action
  changeLang = (opt: SelectOption) => {
    this.fieldState.lang = opt.value as LangCode;
  };

  @action
  changeRanges = (newRanges: ScoreRangeUpdate[]) => {
    this.fieldState.scoreRangeUpdates = newRanges.slice();
  };

  ownerOptions = (): SelectOption[] => {
    const globalOption = { label: 'GLOBAL', value: '' };
    const clientOptions = this.props.roleData.clientList.map(appClient => ({
      label: appClient.name,
      value: appClient.appClientId
    }));
    return [globalOption, ...clientOptions];
  };

  selectedOwnerOption = (owner: Owner): SelectOption => {
    if (owner.kind === 'global') {
      return { label: 'GLOBAL', value: '' };
    } else {
      const option = this.ownerOptions().find(opt => opt.value === owner.value);
      return option !== undefined ? option : { label: 'Error', value: '?' };
    }
  };

  @action
  onSetOwner = (owner: SelectOption) => {
    owner.value === ''
      ? (this.fieldState.owner = { kind: 'global' })
      : (this.fieldState.owner = { kind: 'appClientId', value: `${owner.value}` });
  };

  learningProviderOptions = (): SelectOption[] => {
    let availableProviders = this.props.roleData.providerList;
    // If the owner is a client, we have to limit the options available.
    const owner = this.props.roleData.clientList.find(c => {
      if (this.fieldState.owner.kind === 'appClientId' && c.appClientId === this.fieldState.owner.value) {
        return c;
      }
      return undefined;
    });
    if (owner && owner.providerIds.length > 0) {
      availableProviders = availableProviders.slice().filter(p => owner.providerIds.includes(p.id));
    }
    return availableProviders.map(provider => {
      return { label: provider.value.name, value: provider.id };
    });
  };

  @action
  onSetProviders = (selectedOptions: SelectOption[]) => {
    selectedOptions.length === 0
      ? (this.fieldState.recommendationProviders = [])
      : (this.fieldState.recommendationProviders = selectedOptions.map(option => `${option.value}`));
  };

  selectedProviderOptions = (providerIds: DbKey<LearningProvider>[]): SelectOption[] => {
    const options: SelectOption[] = [];
    providerIds.forEach(id => {
      const option = this.learningProviderOptions().find(opt => opt.value === id);
      if (option !== undefined) {
        options.push(option);
      }
    });
    return options;
  };

  ProviderObserver = observer(() => {
    return this.fieldState.recommendationMethod !== RoleRecommendationMethod.roleRange
      ? null
      : MultiSelectObserver({
        options: this.learningProviderOptions(),
        selectedOptions: this.selectedProviderOptions(this.fieldState.recommendationProviders),
        title: 'Learning Providers for recommendations used with this Role',
        xsSpan: 4,
        onChange: this.onSetProviders,
        disabled: this.fieldState.recommendationMethod !== RoleRecommendationMethod.roleRange,
        debug: !!this.props.debug
      });
  });

  LangSelector = observer(() => {
    const { i18nOptions } = this.props;
    const onChange = this.changeLang;
    const options = i18nOptions();
    return (
      <SelectBox
        options={options}
        defaultValue={options.find(option => (option.value as LangCode) === this.fieldState.lang)}
        onChange={onChange}
        title={'Language'}
        xsSpan={4}
      />
    );
  });

  NameObserver = observer(() =>
    I18nTextInputObserver({
      field: this.fieldState.name,
      type: 'text',
      lang: this.fieldState.lang,
      title: 'Role Name',
      placeholder: `Name of Role`,
      xsSpan: 4,
      debug: this.props.debug
    })
  );

  ShortNameObserver = observer(() =>
    I18nTextInputObserver({
      field: this.fieldState.shortname,
      type: 'text',
      lang: this.fieldState.lang,
      title: 'Role Short Name',
      placeholder: `Shortname of Role (for tight spaces)`,
      xsSpan: 4,
      debug: this.props.debug
    })
  );

  OwnerObserver = observer(() =>
    SelectObserver({
      options: this.ownerOptions(),
      selectedOption: this.selectedOwnerOption(this.fieldState.owner),
      title: 'Owner Client (or Global)',
      xsSpan: 4,
      onChange: this.onSetOwner,
      disabled: !this.props.store.isSuperUser,
      debug: !!this.props.debug
    })
  );

  RadioRelevanceObserver = observer(() => (
    <RadioItem
      label="Relevance/Priority Method"
      onChange={this.onChangeRecommendationMethod('relevance')}
      selected={this.fieldState.recommendationMethod === RoleRecommendationMethod.relevance}
      size={'xxsmall'}
    />
  ));

  RadioRoleRangeObserver = observer(() => (
    <RadioItem
      label="Competency/Role/Score Range Method"
      onChange={this.onChangeRecommendationMethod('roleRange')}
      selected={this.fieldState.recommendationMethod === RoleRecommendationMethod.roleRange}
      size={'xxsmall'}
    />
  ));

  onChangeRecommendationMethod = (radio: 'relevance' | 'roleRange') => (value: boolean) => {
    if (value) {
      switch (radio) {
        case 'relevance':
          runInAction(() => {
            this.fieldState.recommendationMethod = RoleRecommendationMethod.relevance;
            this.fieldState.recommendationProviders = [];
          });
          break;
        case 'roleRange':
          runInAction(() => (this.fieldState.recommendationMethod = RoleRecommendationMethod.roleRange));
      }
    }
  };

  @computed
  get inputIsValid(): boolean {
    const { isI18nValid } = this.props;
    const isValid: boolean =
      isI18nValid(this.fieldState.name) &&
      isI18nValid(this.fieldState.shortname) &&
      this.fieldState.scoreRangeUpdates.every(r => {
        switch (r.kind) {
          case 'create':
            return r.value.scoreRange.label.value.en.length !== 0;
          case 'update':
            return r.value.value.scoreRange.label.value.en.length !== 0;
          default:
            return true;
        }
      }) &&
      (this.fieldState.owner.kind === 'global' || this.fieldState.owner.value.length > 0);
    return isValid;
  }

  onSave = async () => {
    const { item, makeI18nContent, mode, onCreate, onUpdate, onClone } = this.props;
    if (this.inputIsValid) {
      /// Build from scratch here to get latest changes
      const r = makeWorkRole({
        name: makeI18nContent(this.fieldState.name),
        shortname: makeI18nContent(this.fieldState.shortname),
        owner: this.fieldState.owner,
        recommendationMethod: this.fieldState.recommendationMethod,
        recommendationProviders: this.fieldState.recommendationProviders,
        // ignore content just to get this over the line
        useSkillGroupReport: false
      });
      /// Just assume these are all 'create's for a Create
      if (item && mode === 'clone' && onClone) {
        await onClone({
          workRole: {
            id: item.id, // Pass original ID in as needed to find skills
            value: { ...r }
          },
          scoreRangeUpdates: [...this.fieldState.scoreRangeUpdates]
        });
        return;
      }

      await (item && onUpdate
        ? onUpdate({
          workRole: makeWithDbId({
            id: item.id,
            value: { ...r }
          }),
          scoreRangeUpdates: [...this.fieldState.scoreRangeUpdates]
        })
        : onCreate({
          workRole: {
            id: '',
            value: { ...r }
          },
          scoreRangeUpdates: [...this.fieldState.scoreRangeUpdates]
        }));
    }
  };

  Edit = (title: string) => {
    const { debug, item, mode, onCancel } = this.props;
    const subTitle = item && mode === 'clone' ? 'Set a new Role Name' : null;
    const rangeRoleId = item ? item.id : '';
    return (
      <React.Fragment>
        <div className={styles.row}>
          <h4>{title}</h4>
        </div>
        <div className={styles.row}>
          <h5>{subTitle}</h5>
          <this.LangSelector />
        </div>
        <div className={styles.row}>
          <this.NameObserver />
        </div>
        <div className={styles.row}>
          <this.ShortNameObserver />
        </div>
        <div className={styles.row}>
          <this.OwnerObserver />
        </div>
        <div className={styles.row}>
          <this.RadioRelevanceObserver />
        </div>
        <div className={classnames(styles.row, styles.checkItemSet)}>
          <this.RadioRoleRangeObserver />
          <this.ProviderObserver />
        </div>
        <div className={styles.row}>
          <ScoreRangeEditor
            debug={debug}
            lang={this.fieldState.lang}
            roleId={rangeRoleId}
            scoreRanges={this.fieldState.scoreRangeUpdates}
            postUpdates={this.changeRanges}
            onCancel={onCancel}
          />
        </div>
      </React.Fragment>
    );
  };

  render() {
    const { item, mode, onUpdate } = this.props;
    let title = (item && onUpdate ? 'Update' : 'New') + ' Role';
    if (item && mode === 'clone') {
      title = 'Save As';
    }
    return (
      <div className={styles.page}>
        <div className={styles.body}>
          <div>
            {renderButtons(
              this.onSave,
              undefined,
              this.props.onCancel,
              () => this.inputIsValid && !this.locked,
              undefined,
              () => !this.locked
            )}
          </div>
          {this.Edit(title)}
        </div>
      </div>
    );
  }
}

export const WorkRoleEditor = withI18n(WorkRoleEditor0);

interface RoleDeleterProps {
  items?: WithDbId<WorkRole>[];
  onDelete?(ids: DbKey<WorkRole>[]): Promise<void>;
  onCancel(): void;
}

/** Base component to delete Role */
// @observer
export class RoleDeleter extends React.Component<RoleDeleterProps> {
  onDelete = async () => {
    const { items, onDelete } = this.props;
    if (items && onDelete) {
      await onDelete(items.map(item => item.id));
    }
  };

  render() {
    const { onCancel, items } = this.props;
    return items ? (
      <div className={styles.page}>
        <div className={styles.body}>
          <div className={styles.row}>
            {deleteHandler(this.onDelete, onCancel, 'Roles', items.map(item => item.value.name.value.en))}
          </div>
        </div>
      </div>
    ) : null;
  }
}



// WEBPACK FOOTER //
// ./src/admin/ui/components/editors/workrole-editor/workrole-editor.tsx